import { useEffect, useRef, useState } from "react";
import { Headphones, Play, Pause, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const SPEEDS = [0.75, 1, 1.25];
const SPEED_KEY = "yh_listen_speed";
const savedSpeed = () => {
  const v = Number(localStorage.getItem(SPEED_KEY));
  return SPEEDS.includes(v) ? v : 1;
};

// Plays the server-made recording of the current page; the page text itself is never shown.
const usePageAudio = (src, on, speed) => {
  const audio = useRef(null);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (!on) { setStatus("idle"); return undefined; }
    let url;
    let cancelled = false;
    setStatus("loading");
    api.get(src, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        const a = new Audio(url);
        a.playbackRate = speed;
        a.onended = () => setStatus("ended");
        audio.current = a;
        a.play().then(() => setStatus("playing")).catch(() => setStatus("paused"));
      })
      .catch((e) => { if (!cancelled) setStatus(e?.response?.status === 422 ? "empty" : "error"); });
    return () => {
      cancelled = true;
      audio.current?.pause();
      audio.current = null;
      if (url) URL.revokeObjectURL(url);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, on]);

  useEffect(() => { if (audio.current) audio.current.playbackRate = speed; }, [speed]);

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) { a.play(); setStatus("playing"); } else { a.pause(); setStatus("paused"); }
  };
  return { status, toggle };
};

export const ListenToggle = ({ on, onChange, disabled }) => {
  const { t } = useLang();
  return (
    <button onClick={() => onChange(!on)} disabled={disabled} data-testid="reader-listen-toggle" aria-pressed={on}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${on ? "bg-[#B45309] text-white" : "border border-current hover:bg-black/10"} disabled:opacity-40`}>
      <Headphones className="w-4 h-4" /> {on ? t.reader.stopListen : t.reader.listen}
    </button>
  );
};

export const ListenBar = ({ bookId, page, src }) => {
  const { t } = useLang();
  const [speed, setSpeed] = useState(savedSpeed);
  const { status, toggle } = usePageAudio(src || `/tts/${bookId}/${page}/audio`, true, speed);
  const pick = (s) => { setSpeed(s); localStorage.setItem(SPEED_KEY, String(s)); };
  const msg = { loading: t.reader.preparing, empty: t.reader.noText, error: t.reader.ttsError, playing: t.reader.listening, paused: t.reader.paused, ended: t.reader.endedMsg }[status];
  return (
    <div className="flex items-center gap-3 px-4 sm:px-6 py-2.5 bg-[#B45309]/10 border-b border-black/10 text-sm" data-testid="read-aloud-bar">
      <button onClick={toggle} disabled={!["playing", "paused", "ended"].includes(status)} data-testid="read-aloud-play-pause"
        className="w-9 h-9 rounded-full bg-[#1C1917] text-[#FAF7F2] grid place-items-center disabled:opacity-40" aria-label={status === "playing" ? t.reader.paused : t.reader.listen}>
        {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : status === "playing" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <span className="flex-1" data-testid="read-aloud-status">{msg}</span>
      <div className="flex items-center gap-1" role="group" aria-label={t.reader.speed} data-testid="read-aloud-speed">
        <span className="text-xs opacity-70 hidden sm:inline">{t.reader.speed}</span>
        {SPEEDS.map((s) => (
          <button key={s} onClick={() => pick(s)} aria-pressed={speed === s} data-testid={`read-aloud-speed-${s === 0.75 ? "slow" : s === 1 ? "normal" : "fast"}`}
            className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${speed === s ? "bg-[#1C1917] text-white" : "border border-current hover:bg-black/10"}`}>
            {s === 0.75 ? t.reader.slow : s === 1 ? t.reader.normal : t.reader.fast}
          </button>
        ))}
      </div>
    </div>
  );
};
